import React from "react";
import { Box, Tabs, Tab, useMediaQuery, useTheme } from "@mui/material";
import { CustomSectionTitle, colors } from "@ippon/shared-components";
import { useSidebarWidth } from "@ippon/shared-layouts";
import { BuildingIcon, ResidenceIcon } from "../../../common/icons";
import { PROPERTY_DETAIL_TABS } from "../tabConfig";
import {
  PROPERTY_NAME_TOP,
  PROPERTY_NAME_HEIGHT_DESKTOP,
  PROPERTY_NAME_HEIGHT_MOBILE,
  TAB_NAVIGATION_TOP_DESKTOP,
  TAB_NAVIGATION_TOP_MOBILE,
  SCROLLBAR_HEIGHT,
  SCROLLBAR_BORDER_RADIUS,
} from "../../../constants/layout";

interface PropertyDetailHeaderProps {
  propertyName: string;
  tabValue: number;
  onTabChange: (event: React.SyntheticEvent, newValue: number) => void;
  /** 物件カテゴリ (residence/building)。アイコン切替用。 */
  propertyCategory?: string;
}

const PropertyDetailHeader: React.FC<PropertyDetailHeaderProps> = ({
  propertyName,
  tabValue,
  onTabChange,
  propertyCategory,
}) => {
  const sidebarWidth = useSidebarWidth();
  const theme = useTheme();
  // lg 未満ではサイドバー非表示のため left/width の transition を付けない（右からスライドに見えるのを防ぐ）
  const isLgUp = useMediaQuery(theme.breakpoints.up("lg"));
  const mainHeaderTransition = isLgUp
    ? "left 0.2s cubic-bezier(0.4, 0, 0.2, 1), width 0.2s cubic-bezier(0.4, 0, 0.2, 1)"
    : "none";

  return (
    <>
      {/* 物件名とステータス */}
      <Box
        sx={{
          position: "fixed",
          top: `${PROPERTY_NAME_TOP}px`,
          left: `${sidebarWidth}px`,
          right: 0,
          zIndex: theme.zIndex.propertyName,
          backgroundColor: colors.background.default,
          width: `calc(100vw - ${sidebarWidth}px)`,
          height: {
            lg: `${PROPERTY_NAME_HEIGHT_DESKTOP}px`,
            xs: `${PROPERTY_NAME_HEIGHT_MOBILE}px`,
          },
          pt: 0,
          pb: 0.5,
          transition: mainHeaderTransition,
        }}
      >
        <Box
          sx={{
            pl: 3,
            pr: 3,
          }}
          data-field="name"
          data-label="物件名"
        >
          <CustomSectionTitle
            title={propertyName}
            size="large"
            color={colors.text.primary}
            fontFamily='"Noto Sans JP"'
            sx={{ mt: 1 }}
            fontWeight={700}
            addBorder={false}
            icon={
              propertyCategory === "residence" ? (
                <ResidenceIcon size={28} />
              ) : (
                <BuildingIcon size={28} />
              )
            }
          />
        </Box>
      </Box>

      {/* タブナビゲーション */}
      <Box
        sx={{
          backgroundColor: colors.background.default,
          position: "fixed",
          top: {
            lg: `${TAB_NAVIGATION_TOP_DESKTOP}px`,
            xs: `${TAB_NAVIGATION_TOP_MOBILE}px`,
          },
          left: `${sidebarWidth}px`,
          right: 0,
          zIndex: theme.zIndex.propertyTabs,
          width: `calc(100vw - ${sidebarWidth}px)`,
          overflowX: "auto",
          transition: mainHeaderTransition,
          "&::-webkit-scrollbar": {
            height: `${SCROLLBAR_HEIGHT}px`,
          },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: colors.border.dark,
            borderRadius: `${SCROLLBAR_BORDER_RADIUS}px`,
          },
        }}
      >
        <Box
          sx={{
            px: { lg: 2, xs: 3 },
            pl: { lg: 2, xs: 2 },
            pr: { lg: 3, xs: 3 },
          }}
        >
          <Tabs
            value={tabValue}
            onChange={onTabChange}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              minHeight: "30px",
              borderBottom: `1px solid ${colors.border.tab}`,
              "& .MuiTabs-indicator": {
                display: "none",
              },
              "& .MuiTab-root": {
                position: "relative",
                minHeight: "30px",
                paddingTop: 0.5,
                paddingBottom: 0.5,
                paddingLeft: 2,
                paddingRight: 2,
                minWidth: "auto",
                textTransform: "none",
                fontFamily: '"Noto Sans JP"',
                fontSize: "14px",
                fontWeight: 700,
                lineHeight: "1.7",
                color: colors.text.secondary,
                "&:hover": {
                  color: colors.primary.main,
                  opacity: 1,
                },
                "&.Mui-selected": {
                  color: colors.primary.main,
                  "&::after": {
                    content: '""',
                    position: "absolute",
                    bottom: 0,
                    left: 0,
                    right: 0,
                    height: "2px",
                    backgroundColor: colors.primary.main,
                    borderRadius: "10px 10px 0 0",
                  },
                },
              },
              "& .MuiTabs-scrollButtons": {
                color: colors.primary.main,
                "&.Mui-disabled": {
                  opacity: 0.3,
                },
              },
            }}
          >
            {PROPERTY_DETAIL_TABS.map((tab) => (
              <Tab key={tab.id} label={tab.label} value={tab.id} />
            ))}
          </Tabs>
        </Box>
      </Box>
    </>
  );
};

export default PropertyDetailHeader;
